import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, BadgeCheck, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ProfileView } from "@/components/account/ProfileView";
import VeriffVerification from "@/components/booking/VeriffVerification";

export function CustomerProfile({ me, onSaved }) {
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [bio, setBio] = useState("");
  const [city, setCity] = useState("");
  const [saving, setSaving] = useState(false);
  const [identity, setIdentity] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const loadIdentity = () => api.get("/veriff/me").then((r) => setIdentity(r.data)).catch(() => {});

  useEffect(() => {
    if (!me?.id) return;
    api.get(`/users/${me.id}/profile`).then((r) => setProfile(r.data)).catch(() => {});
    loadIdentity();
    setBio(me.bio || "");
    setCity(me.city || "");
  }, [me?.id, me?.bio, me?.city]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile/me", { bio, city });
      toast.success(t("customerProfile.profileUpdated"));
      onSaved?.();
      const r = await api.get(`/users/${me.id}/profile`);
      setProfile(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <div data-testid="identity-card" className="bg-white rounded-2xl shadow-card border border-slate-100 p-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><ShieldCheck size={18} className="text-blue-600" /> {t("veriff.title")}</h3>
          {identity?.identity_verified ? (
            <span data-testid="identity-verified-badge" className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 text-xs font-bold px-3 py-1.5 rounded-full"><BadgeCheck size={14} /> {t("veriff.verifiedBadge")}</span>
          ) : (
            <span data-testid="identity-unverified-badge" className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-500 text-xs font-bold px-3 py-1.5 rounded-full">{t("veriff.notVerified")}</span>
          )}
        </div>
        {identity?.identity_verified && identity?.license_data ? (
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4 text-sm" data-testid="license-details">
            {[
              [t("veriff.legalName"), identity.license_data.full_name],
              [t("veriff.dob"), identity.license_data.date_of_birth],
              [t("veriff.licenseNumber"), identity.license_data.document_number],
              [t("veriff.issuedBy"), [identity.license_data.issuing_state, identity.license_data.issuing_country].filter(Boolean).join(", ")],
              [t("veriff.expires"), identity.license_data.expiration_date],
              [t("veriff.verifiedOn"), (identity.identity_verified_at || "").slice(0, 10)],
            ].filter(([, v]) => v).map(([label, value]) => (
              <div key={label}>
                <p className="label-caps text-slate-400 text-[10px]">{label}</p>
                <p className="text-slate-800 font-medium mt-0.5">{value}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-4">
            <p className="text-slate-500 text-sm">{identity?.reverification_required ? t("veriff.reverifyNeeded") : t("veriff.profileIntro")}</p>
            <button onClick={() => setVerifying(true)} data-testid="profile-verify-now" className="mt-4 bg-blue-600 text-white px-6 py-2.5 rounded-full font-semibold text-sm hover:bg-blue-700 transition-colors">
              {t("veriff.verifyNow")}
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-card border border-slate-100 p-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
          <h3 className="font-heading text-lg font-semibold">{t("customerProfile.editProfile")}</h3>
          {me?.id && (
            <Link to={`/u/${me.id}`} target="_blank" data-testid="view-public-profile" className="text-sm font-semibold text-blue-600 hover:text-blue-700 inline-flex items-center gap-1.5">
              {t("customerProfile.viewPublic")} <ExternalLink size={14} />
            </Link>
          )}
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700">{t("customerProfile.city")}</label>
            <input value={city} onChange={(e) => setCity(e.target.value)} data-testid="profile-city" className="belo-input w-full mt-1.5" placeholder={t("customerProfile.cityPlaceholder")} />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">{t("customerProfile.aboutYou")}</label>
            <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={4} data-testid="profile-bio" className="belo-input w-full mt-1.5 resize-none" placeholder={t("customerProfile.bioPlaceholder")} />
          </div>
          <button onClick={save} disabled={saving} data-testid="profile-save" className="rounded-full bg-blue-600 text-white px-6 py-2.5 font-semibold text-sm hover:bg-blue-700 disabled:opacity-50">{t("customerProfile.saveChanges")}</button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-card border border-slate-100 p-8">
        <ProfileView profile={profile} />
      </div>

      {verifying && (
        <VeriffVerification
          createSession={() => api.post("/veriff/session").then((r) => r.data)}
          onVerified={() => { setVerifying(false); loadIdentity(); toast.success(t("veriff.approved")); }}
          onClose={() => setVerifying(false)}
        />
      )}
    </div>
  );
}
